/**
 * Approvals inbox — one façade over the module approval endpoints (spec 21 §3.5.3).
 *
 * The API has no aggregate `/approvals` endpoint yet (→ A30), so the inbox reads each
 * module's own pending list and normalises it here. Each source is fetched and reported
 * separately: one module failing shows as an error on THAT source, never as "all caught
 * up" (Principle 0-A §3). Decisions go to the module endpoints the record pages use.
 */
import { api } from './client'
import type { Role } from '@/types/auth'

export type ApprovalType = 'leave' | 'reimbursement' | 'loan' | 'regularisation' | 'overtime' | 'exit' | 'profile' | 'workflow'
export type Decision = 'APPROVE' | 'REJECT'

export interface ApprovalItem {
  /** Unique across sources: `${type}:${id}`. */
  key: string
  type: ApprovalType
  id: string
  employeeId: string | null
  employeeName: string | null
  employeeCode: string | null
  title: string
  /** Up to four type-specific facts shown in the row and the detail pane. */
  fields: { label: string; value: string }[]
  amountPaise?: number
  requestedAt: string | null
  /** The canonical record page. */
  detailPath: string | null
  /** Decisions this inbox can take on the item; anything else happens on the record page. */
  decisions: Decision[]
  /** The module rejects without a reason, so the inbox requires one (spec §3.5.4: ≥10 chars). */
  reasonRequiredFor: Decision[]
}

export interface ApprovalSource {
  type: ApprovalType
  label: string
  /** Roles the API lets act on this type (SUPER_ADMIN passes every check). */
  roles: Role[]
  fetch: () => Promise<ApprovalItem[]>
  decide: (item: ApprovalItem, decision: Decision, note: string) => Promise<void>
}

type Row = Record<string, unknown>
const s = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : typeof v === 'number' ? String(v) : null)

function itemsOf(data: unknown): Row[] {
  if (Array.isArray(data)) return data as Row[]
  const d = data as { items?: Row[]; data?: Row[] } | null
  return d?.items ?? d?.data ?? []
}

async function list(path: string, params: Record<string, string | number | boolean>): Promise<Row[]> {
  const r = await api.get(path, { params: { per_page: 100, limit: 100, ...params } })
  return itemsOf(r.data)
}

function fullName(r: Row): string | null {
  const e = r.employee as Row | null | undefined
  const joined = e ? `${s(e.first_name) ?? ''} ${s(e.last_name) ?? ''}`.trim() : ''
  return s(r.employee_name) ?? (joined || null)
}

function days(n: unknown): string {
  const v = Number(n)
  if (!Number.isFinite(v)) return '—'
  return `${v} day${v === 1 ? '' : 's'}`
}

const HR: Role[] = ['HR_ADMIN', 'HR_EXECUTIVE']
const HR_AND_MANAGER: Role[] = ['HR_ADMIN', 'HR_EXECUTIVE', 'MANAGER']

const APPROVAL_SOURCES: ApprovalSource[] = [
  {
    type: 'leave',
    label: 'Leave',
    roles: HR_AND_MANAGER,
    fetch: async () => {
      const [a, b] = await Promise.all([
        list('/api/v1/leave/requests', { status: 'PENDING' }),
        list('/api/v1/leave/requests', { status: 'PENDING_MANAGER' }),
      ])
      // `can_act` is the server's per-user answer (stage, direct report, never your own).
      return [...a, ...b].filter((r) => r.can_act !== false).map((r): ApprovalItem => ({
        key: `leave:${r.id}`,
        type: 'leave',
        id: String(r.id),
        employeeId: s(r.employee_id),
        employeeName: fullName(r),
        employeeCode: s(r.employee_code),
        title: `${s(r.leave_type_name) ?? 'Leave'} · ${days(r.days)}`,
        fields: [
          { label: 'From', value: s(r.start_date) ?? '—' },
          { label: 'To', value: s(r.end_date) ?? '—' },
          { label: 'Stage', value: r.status === 'PENDING_MANAGER' ? 'Manager approval' : 'HR approval' },
          ...(s(r.reason) ? [{ label: 'Reason', value: s(r.reason)! }] : []),
        ],
        requestedAt: s(r.created_at),
        detailPath: `/leave/${r.id}`,
        decisions: ['APPROVE', 'REJECT'],
        reasonRequiredFor: ['REJECT'],
      }))
    },
    decide: async (item, decision, note) => {
      await api.post(`/api/v1/leave/requests/${item.id}/action`, {
        action: decision,
        ...(note ? { rejection_note: note } : {}),
      })
    },
  },
  {
    type: 'reimbursement',
    label: 'Reimbursements',
    roles: HR_AND_MANAGER,
    fetch: async () => {
      const rows = await list('/api/v1/reimbursements', { status: 'PENDING' })
      return rows.map((r): ApprovalItem => ({
        key: `reimbursement:${r.id}`,
        type: 'reimbursement',
        id: String(r.id),
        employeeId: s(r.employee_id),
        employeeName: fullName(r),
        employeeCode: s(r.employee_code),
        title: s(r.title) ?? 'Expense claim',
        fields: [
          { label: 'Claim', value: s(r.claim_number) ?? '—' },
          { label: 'Category', value: s(r.category) ?? '—' },
          { label: 'Claim date', value: s(r.claim_date) ?? '—' },
          ...(s(r.description) ? [{ label: 'Note', value: s(r.description)! }] : []),
        ],
        amountPaise: Number(r.amount ?? 0),
        requestedAt: s(r.created_at),
        detailPath: `/reimbursements/${r.id}`,
        decisions: ['APPROVE', 'REJECT'],
        reasonRequiredFor: ['REJECT'],
      }))
    },
    decide: async (item, decision, note) => {
      if (decision === 'APPROVE') await api.post(`/api/v1/reimbursements/${item.id}/approve`, {})
      else await api.post(`/api/v1/reimbursements/${item.id}/reject`, { rejection_note: note })
    },
  },
  {
    type: 'loan',
    label: 'Loans',
    roles: HR,
    fetch: async () => {
      const rows = await list('/api/v1/loans', { status: 'APPLIED' })
      return rows.map((r): ApprovalItem => ({
        key: `loan:${r.id}`,
        type: 'loan',
        id: String(r.id),
        employeeId: s(r.employee_id),
        employeeName: fullName(r),
        employeeCode: s(r.employee_code),
        title: `${(s(r.loan_type) ?? 'Loan').replace(/_/g, ' ').toLowerCase().replace(/^\w/, (m) => m.toUpperCase())}`,
        fields: [
          { label: 'Loan', value: s(r.loan_number) ?? '—' },
          { label: 'Tenure', value: r.tenure != null ? `${r.tenure} months` : '—' },
          { label: 'Interest', value: r.interest_rate != null ? `${r.interest_rate}%` : '—' },
          ...(s(r.reason) ? [{ label: 'Reason', value: s(r.reason)! }] : []),
        ],
        amountPaise: Number(r.principal_amount ?? 0),
        requestedAt: s(r.created_at),
        detailPath: `/loans/${r.id}`,
        decisions: ['APPROVE', 'REJECT'],
        reasonRequiredFor: ['REJECT'],
      }))
    },
    decide: async (item, decision, note) => {
      if (decision === 'APPROVE') await api.post(`/api/v1/loans/${item.id}/approve`, {})
      else await api.post(`/api/v1/loans/${item.id}/reject`, { note })
    },
  },
  {
    type: 'regularisation',
    label: 'Attendance regularisation',
    roles: HR_AND_MANAGER,
    fetch: async () => {
      // Requests live in attendance_regularizations (one row per request, own status).
      // `can_act` is the server's answer for THIS user (e.g. never your own request).
      const rows = await list('/api/v1/attendance/regularizations', { status: 'PENDING' })
      return rows
        .filter((r) => r.can_act !== false && s(r.attendance_record_id))
        .map((r): ApprovalItem => ({
          key: `regularisation:${r.id}`,
          type: 'regularisation',
          id: String(r.attendance_record_id),
          employeeId: s(r.employee_id),
          employeeName: fullName(r),
          employeeCode: s(r.employee_code),
          title: `Regularise ${s(r.date) ?? 'attendance'}`,
          fields: [
            { label: 'Date', value: s(r.date) ?? '—' },
            { label: 'Check-in', value: s(r.requested_check_in)?.slice(11, 16) ?? '—' },
            { label: 'Check-out', value: s(r.requested_check_out)?.slice(11, 16) ?? '—' },
            ...(s(r.reason) ? [{ label: 'Reason', value: s(r.reason)! }] : []),
          ],
          requestedAt: s(r.created_at),
          detailPath: `/attendance/regularize/${r.attendance_record_id}`,
          decisions: ['APPROVE', 'REJECT'],
          reasonRequiredFor: ['REJECT'],
        }))
    },
    decide: async (item, decision, note) => {
      await api.post(`/api/v1/attendance/${item.id}/regularize-action`, { action: decision, ...(note ? { note } : {}) })
    },
  },
  {
    // T2-053: pending overtime claims. `can_act` is the server's ApprovalGate answer for THIS user
    // (the approver the OT rule names; never the claimant), so the inbox lists only what they may decide.
    type: 'overtime',
    label: 'Overtime',
    roles: HR_AND_MANAGER,
    fetch: async () => {
      const rows = await list('/api/v1/overtime', { status: 'PENDING' })
      return rows
        .filter((r) => r.can_act !== false)
        .map((r): ApprovalItem => ({
          key: `overtime:${r.id}`,
          type: 'overtime',
          id: String(r.id),
          employeeId: s(r.employee_id),
          employeeName: fullName(r),
          employeeCode: s(r.employee_code),
          title: `Overtime · ${Number(r.hours ?? 0)} h`,
          fields: [
            { label: 'Date', value: s(r.date) ?? '—' },
            { label: 'Hours', value: r.hours != null ? `${Number(r.hours)} h` : '—' },
            { label: 'Rate', value: r.rate_multiplier != null ? `${Number(r.rate_multiplier)}×` : '—' },
          ],
          amountPaise: r.amount_paise != null ? Number(r.amount_paise) : undefined,
          requestedAt: s(r.created_at),
          detailPath: '/overtime',
          decisions: ['APPROVE', 'REJECT'],
          // The overtime API records no rejection reason, so none is asked for.
          reasonRequiredFor: [],
        }))
    },
    decide: async (item, decision) => {
      await api.post(`/api/v1/overtime/${item.id}/action`, { action: decision })
    },
  },
  {
    type: 'exit',
    label: 'Exit requests',
    roles: HR_AND_MANAGER,
    fetch: async () => {
      const rows = await list('/api/v1/offboarding/exit-requests', { status: 'SUBMITTED' })
      return rows.map((r): ApprovalItem => ({
        key: `exit:${r.id}`,
        type: 'exit',
        id: String(r.id),
        employeeId: s(r.employee_id),
        employeeName: fullName(r),
        employeeCode: s((r.employee as Row | undefined)?.employee_code),
        title: 'Resignation',
        fields: [
          { label: 'Resigned on', value: s(r.resignation_date) ?? '—' },
          { label: 'Last working day', value: s(r.last_working_date) ?? '—' },
          { label: 'Notice', value: r.notice_period_days != null ? days(r.notice_period_days) : '—' },
          ...(s(r.exit_reason) ? [{ label: 'Reason', value: s(r.exit_reason)! }] : []),
        ],
        requestedAt: s(r.created_at),
        detailPath: `/offboarding/${r.id}`,
        // The API has no reject transition for a submitted exit (SUBMITTED → APPROVED|REVOKED);
        // revoking is the employee's withdrawal and lives on the record page.
        decisions: ['APPROVE'],
        reasonRequiredFor: [],
      }))
    },
    decide: async (item, decision) => {
      if (decision !== 'APPROVE') throw new Error('Exit requests can only be approved here. Open the record for other actions.')
      await api.post(`/api/v1/offboarding/exit-requests/${item.id}/approve`, {})
    },
  },
  {
    type: 'profile',
    label: 'Profile changes',
    roles: HR_AND_MANAGER,
    fetch: async () => {
      // Approval-gated profile edits (bank account, PAN, name, nominations — Addendum A2).
      // Sensitive values arrive masked; revealing them is an audited action on the
      // Profile approvals page, so the inbox never shows more than the API masks.
      const rows = await list('/api/v1/profile/change-requests', { status: 'PENDING' })
      return rows.map((r): ApprovalItem => {
        const emp = (r.employee ?? null) as Row | null
        const sensitive = Boolean(r.has_sensitive_values)
        return {
          key: `profile:${r.id}`,
          type: 'profile',
          id: String(r.id),
          employeeId: s(r.employee_id),
          employeeName: s(emp?.name),
          employeeCode: s(emp?.employee_code),
          title: `${s(r.label) ?? s(r.field) ?? 'Profile'} change`,
          fields: [
            { label: 'Current', value: s(r.old_value) ?? '—' },
            { label: 'Proposed', value: s(r.new_value) ?? '—' },
            { label: 'Proof', value: r.has_proof ? 'Attached' : 'None' },
            ...(sensitive ? [{ label: 'Note', value: 'Values are masked. Reveal them (audited) from Profile approvals.' }] : []),
          ],
          requestedAt: s(r.requested_at),
          detailPath: '/profile-approvals',
          decisions: ['APPROVE', 'REJECT'],
          reasonRequiredFor: ['REJECT'],
        }
      })
    },
    decide: async (item, decision, note) => {
      if (decision === 'APPROVE') await api.post(`/api/v1/profile/change-requests/${item.id}/approve`, {})
      else await api.post(`/api/v1/profile/change-requests/${item.id}/reject`, { reason: note })
    },
  },
  {
    type: 'workflow',
    label: 'Workflow steps',
    roles: [],
    fetch: async () => {
      const r = await api.get('/api/v1/workflows/approvals/pending')
      return itemsOf(r.data).map((w): ApprovalItem => {
        const trigger = (w.trigger_data ?? {}) as Row
        return {
          key: `workflow:${w.approval_id}`,
          type: 'workflow',
          id: String(w.instance_id),
          employeeId: s(trigger.employee_id),
          employeeName: s(trigger.employee_name),
          employeeCode: null,
          title: s(trigger.title) ?? s(trigger.subject) ?? 'Workflow approval',
          fields: Object.entries(trigger)
            .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
            .slice(0, 4)
            .map(([k, v]) => ({ label: k.replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase()), value: String(v) })),
          requestedAt: null,
          detailPath: null,
          decisions: ['APPROVE', 'REJECT'],
          reasonRequiredFor: ['REJECT'],
        }
      })
    },
    decide: async (item, decision, note) => {
      const path = decision === 'APPROVE' ? 'approve' : 'reject'
      await api.post(`/api/v1/workflows/instances/${item.id}/${path}`, note ? { comments: note } : {})
    },
  },
]

export const approvalKeys = {
  all: ['approvals'] as const,
  source: (type: ApprovalType) => ['approvals', type] as const,
}

/** Sources the given role can act on. An empty `roles` list means every signed-in user. */
export function sourcesFor(hasRole: (roles: Role[]) => boolean): ApprovalSource[] {
  return APPROVAL_SOURCES.filter((src) => src.roles.length === 0 || hasRole(src.roles))
}

/** The caller's own employee id: nobody may decide their own request (ApprovalGate). */
export async function fetchMyEmployeeId(): Promise<string | null> {
  try {
    const r = await api.get('/api/v1/me/profile')
    const d = r.data as { employee?: { id?: string } | null } | null
    return d?.employee?.id ?? null
  } catch {
    return null // no linked employee (e.g. platform admin): nothing to exclude
  }
}
